import React, { useCallback, useMemo } from "react";
import { isMobile } from "react-device-detect";
import { Lock } from "lucide-react";
import { useBrand } from "context/BrandContext";
import { AiChatType, AppBrand, InputSuggestionType, NavProfile, UserRoleValue } from "constants/field-values";
import { AdminNavPaywallSource } from "constants/constants";
import ChatWindow from "components/chat-window/chat-window";
import AdminNavPaywallGate from "components/admin-nav-paywall-gate/admin-nav-paywall-gate";
import { useAiChatStatus } from "state/reactive-vars/ai-chat-status-var";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useNavProfile } from "hooks/useNavProfile";
import { useGetUserWebAiChatsQuery } from "graphql/queries/get-user-web-ai-chats.generated";

// Both vendor roles get the same paywall (MWA-841 covers vendor_manager and
// vendor_employee equally); only customer_manager sees the customer copy (MWA-842 —
// customer_employee never reaches this nav item at all, see route-permissions.ts).
const VENDOR_PAYWALL_SUBTITLE =
	"See what your customers are asking about when chatting with your AI support agent or sharing feedback about your systems and devices.";

const INTELLIGENCE_PAYWALL_SUBTITLE_BY_ROLE: Partial<Record<UserRoleValue, string>> = {
	[UserRoleValue.vendor_manager]: VENDOR_PAYWALL_SUBTITLE,
	[UserRoleValue.vendor_employee]: VENDOR_PAYWALL_SUBTITLE,
	[UserRoleValue.customer_manager]:
		"See what your team has been asking about when chatting with your AI support agent or sharing feedback about systems and devices across your properties."
};

// The actual Intelligence chat. Split out from Intelligence below so a paywalled role
// (MWA-841/842) never mounts its data hooks in the first place.
const IntelligencePage: React.FC = () => {
	const { brand } = useBrand();
	const { loggedUserValue } = useLoggedUser();
	const { aiChatStatusValue, updateAiChatStatus } = useAiChatStatus(AiChatType.ADMIN);
	const { chatId = "", historyOpen = false } = aiChatStatusValue;

	const { data: adminChatsData } = useGetUserWebAiChatsQuery({
		variables: { loggedUserId: loggedUserValue.id, chat_type: AiChatType.ADMIN },
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true
	});

	const handleOnOpenChat = useCallback(
		(doctrine_chat_id: string) => {
			if (chatId !== doctrine_chat_id) {
				updateAiChatStatus({
					chatId: doctrine_chat_id,
					chatMessages: [],
					chatTopic: "",
					selected_properties_ids: [],
					installed_product_id: "",
					shop_product_id: "",
					historyOpen: false
				});
			}
		},
		[updateAiChatStatus, chatId]
	);

	const toggleHistory = useCallback(() => {
		updateAiChatStatus({ historyOpen: !historyOpen });
	}, [updateAiChatStatus, historyOpen]);

	const chatHistoryElements = useMemo(() => {
		const { ai_chats = [] } = adminChatsData || {};
		const today: JSX.Element[] = [];
		const previous10: JSX.Element[] = [];
		const previous30: JSX.Element[] = [];
		const olders: JSX.Element[] = [];

		ai_chats.forEach(element => {
			const { id, doctrine_chat_id, starter_message, doctrine_title, created_at } = element || {};
			const diff = new Date().getTime() - new Date(created_at).getTime();
			const hours = Math.floor(diff / (1000 * 60 * 60));
			const days = Math.floor(diff / (1000 * 60 * 60 * 24));
			const chatTitle = doctrine_title || starter_message || "";
			const shortTitle = chatTitle.length > 33 ? chatTitle.substring(0, 33) + "..." : chatTitle;
			const el = (
				<span
					key={`admin-history-${id}`}
					className="ai-chat-side-history-item"
					onClick={() => handleOnOpenChat(doctrine_chat_id)}
				>
					{shortTitle}
				</span>
			);
			if (hours < 24) today.push(el);
			else if (days <= 10) previous10.push(el);
			else if (days < 30) previous30.push(el);
			else olders.push(el);
		});

		return (
			<div className="ai-chat-side-container">
				<div className="ai-chat-side-header">
					<span className="ai-chat-side-header-title">Chat History</span>
					<img
						src={process.env.PUBLIC_URL + "/assets/images/icons/x.svg"}
						className="ai-chat-side-header-close-icon"
						onClick={toggleHistory}
					/>
				</div>
				{today.length > 0 && (
					<div className="ai-chat-side-history-section">
						<span className="ai-chat-side-history-title">Today</span>
						{today}
					</div>
				)}
				{previous10.length > 0 && (
					<div className="ai-chat-side-history-section">
						<span className="ai-chat-side-history-title">Previous 10 days</span>
						{previous10}
					</div>
				)}
				{previous30.length > 0 && (
					<div className="ai-chat-side-history-section">
						<span className="ai-chat-side-history-title">Previous 30 Days</span>
						{previous30}
					</div>
				)}
				{olders.length > 0 && (
					<div className="ai-chat-side-history-section">
						<span className="ai-chat-side-history-title">Older than 30 days</span>
						{olders}
					</div>
				)}
			</div>
		);
	}, [adminChatsData, handleOnOpenChat, toggleHistory]);

	const chatWindow = (
		<ChatWindow
			chatHeaderDescription="Instead of looking through a bunch of reports and graphs, you can ask me questions about your team's knowledge and feedback, based on how they've been using the platform."
			withFilters={false}
			chatType={InputSuggestionType.INTELLIGENCE_QUERY}
			chatTypeMode={AiChatType.ADMIN}
			autoStartMessage="Give me a snapshot of recent topics and support conversations from my team."
		/>
	);

	return (
		<div className="dashboard-container">
			<div className="dashboard-content-container">
				<div className="dashboard-chat-container">
					<span className="dashboard-title">
						{brand === AppBrand.Highnote ? "HiGnome AI Assistant" : "Maestro AI Assistant"}
					</span>
					{isMobile ? (
						historyOpen ? (
							chatHistoryElements
						) : (
							chatWindow
						)
					) : (
						<div className="ai-chat-content-container">
							{chatWindow}
							{historyOpen ? chatHistoryElements : null}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

const Intelligence: React.FC = () => {
	const { loggedUserValue } = useLoggedUser();
	const { navProfile } = useNavProfile();

	const paywallSubtitle = INTELLIGENCE_PAYWALL_SUBTITLE_BY_ROLE[loggedUserValue.role as UserRoleValue];

	if (navProfile.id === NavProfile.Ollie && paywallSubtitle) {
		return (
			<AdminNavPaywallGate
				source={AdminNavPaywallSource.INTELLIGENCE}
				icon={<Lock className="anpg-icon-lock" />}
				title="Unlock Intelligence"
				subtitle={paywallSubtitle}
			/>
		);
	}

	return <IntelligencePage />;
};

export default Intelligence;
